import React, { useState, useEffect, useRef } from "react";
import {
  Heart, AlertTriangle, Lightbulb, Plus, Trash2, Trophy,
  History, ChevronDown, ChevronUp, Loader2, Search, X,
  CheckCircle2, FlaskConical, WifiOff, Database, Star, Zap,
} from "lucide-react";
import { supabase } from "./supabase";
import type { Language } from "./data";
import { checkCreditsForAction, getCreditsForEndpoint } from "./hooks/useCredits";
import { authBearerHeader } from "./utils/authGate";

function isNetworkError(err: unknown): boolean {
  if (!navigator.onLine) return true;
  if (err instanceof TypeError && /fetch|network|Failed to fetch/i.test(err.message)) return true;
  return false;
}

export interface HealthScoreResult {
  food_name: string;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  sugar: number | null;
  sodium: number | null;
  fiber: number | null;
  // Extended nutrients
  saturatedFat?: number;
  cholesterol?: number;
  potassium?: number;
  calcium?: number;
  iron?: number;
  magnesium?: number;
  phosphorus?: number;
  zinc?: number;
  // Scores
  healthy_score: number;
  nutritionDensity?: number;
  proteinQuality?: string;
  // Analysis
  analysis: string;
  warnings: string[];
  suggestions: string[];
  // Vitamins & minerals
  vitamins?: Array<{ key?: string; name: string; amount: number; unit: string; daily_pct: number }>;
  // USDA metadata
  dataSource?: "USDA FoodData Central" | "AI Estimate";
  usdaFdcId?: number;
  usdaDescription?: string;
  usdaDataType?: string;
  servingSize?: number;
  servingDescription?: string;
  per100g?: Record<string, number>;
  dailyValues?: Record<string, number>;
}

interface HistoryItem {
  id: string;
  food_text: string;
  result: HealthScoreResult;
  created_at: string;
}

interface Props {
  lang: Language;
  t: (k: string) => string;
  dir: "rtl" | "ltr";
  userGoal?: string;
}

function getDeviceId(): string {
  let id = localStorage.getItem("device_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("device_id", id); }
  return id;
}

// ─── Score gauge ──────────────────────────────────────────────────────────────
function ScoreGauge({ score, size = 140 }: { score: number; size?: number }) {
  const radius = (size - 24) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = score >= 75 ? "#16a34a" : score >= 55 ? "#65a30d" : score >= 35 ? "#d97706" : "#dc2626";
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={12} className="dark:stroke-slate-700" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={12}
          strokeDasharray={`${progress} ${circumference}`} strokeLinecap="round"
          style={{ transition: "stroke-dasharray 1s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black" style={{ color }}>{score}</span>
        <span className="text-xs text-stone-400 dark:text-slate-500 font-medium">/100</span>
      </div>
    </div>
  );
}

function ScoreLabel({ score, t }: { score: number; t: (k: string) => string }) {
  if (score >= 75) return <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">{t("scoreHealthy")}</span>;
  if (score >= 55) return <span className="text-lime-600 dark:text-lime-400 font-bold text-sm">{t("scoreGood")}</span>;
  if (score >= 35) return <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">{t("scoreFair")}</span>;
  return <span className="text-red-600 dark:text-red-400 font-bold text-sm">{t("scorePoor")}</span>;
}

// ─── Nutrition pill ───────────────────────────────────────────────────────────
function NutritionPill({ label, value, unit, color, dvPct }: {
  label: string; value: number | null; unit: string; color: string; dvPct?: number;
}) {
  return (
    <div className={`flex flex-col items-center gap-0.5 bg-white dark:bg-slate-800 rounded-xl px-3 py-2.5 border ${color} shadow-sm`}>
      <span className="text-lg font-black text-stone-800 dark:text-slate-100 tabular-nums">{value ?? "—"}</span>
      <span className="text-[10px] font-medium text-stone-500 dark:text-slate-400 uppercase tracking-wide">{unit}</span>
      <span className="text-[10px] text-stone-400 dark:text-slate-500 leading-tight text-center">{label}</span>
      {value != null && dvPct !== undefined && dvPct >= 0 && (
        <span className="text-[9px] font-bold text-stone-400 dark:text-slate-500">{dvPct}% DV</span>
      )}
    </div>
  );
}

// ─── Mini score badge ─────────────────────────────────────────────────────────
function ScoreBadge({ label, value, icon, color }: { label: string; value: string | number; icon: React.ReactNode; color: string }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${color}`}>
      <span className="text-lg">{icon}</span>
      <div>
        <p className="text-xs font-black text-stone-800 dark:text-slate-100 leading-none">{value}</p>
        <p className="text-[10px] text-stone-400 dark:text-slate-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

// ─── USDA source badge ────────────────────────────────────────────────────────
function DataSourceBadge({ result, dir }: { result: HealthScoreResult; dir: "rtl" | "ltr" }) {
  const isUSDA = result.dataSource === "USDA FoodData Central";
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold ${
      isUSDA
        ? "bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-300"
        : "bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
    }`} dir={dir}>
      <Database className="w-3.5 h-3.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {isUSDA ? (
          <>
            <span>USDA FoodData Central</span>
            {result.usdaDataType && (
              <span className="ms-1.5 opacity-70 font-normal">· {result.usdaDataType}</span>
            )}
            {result.usdaFdcId && (
              <span className="ms-1.5 opacity-70 font-normal">· FDC ID: {result.usdaFdcId}</span>
            )}
          </>
        ) : (
          <span>AI Estimate — no USDA match found</span>
        )}
      </div>
      {isUSDA && <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-blue-500" />}
    </div>
  );
}

// ─── Vitamins & minerals grid ─────────────────────────────────────────────────
function VitaminsGrid({ vitamins, dir, label }: {
  vitamins: Array<{ name: string; amount: number; unit: string; daily_pct: number }>;
  dir: "rtl" | "ltr";
  label: string;
}) {
  if (!vitamins?.length) return null;
  return (
    <div className="space-y-2.5" dir={dir}>
      <p className="text-xs font-semibold text-stone-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
        <FlaskConical className="w-3.5 h-3.5 text-violet-500" />{label}
      </p>
      <div className="grid grid-cols-2 gap-2">
        {vitamins.map((v, i) => {
          const pct = Math.min(v.daily_pct, 100);
          const barColor = pct >= 50 ? "bg-emerald-500" : pct >= 20 ? "bg-amber-400" : pct >= 5 ? "bg-sky-400" : "bg-stone-300 dark:bg-slate-600";
          const textColor = pct >= 50 ? "text-emerald-700 dark:text-emerald-400" : pct >= 20 ? "text-amber-700 dark:text-amber-400" : pct >= 5 ? "text-sky-700 dark:text-sky-400" : "text-stone-400 dark:text-slate-500";
          return (
            <div key={i} className="bg-white dark:bg-slate-700/50 rounded-xl px-3 py-2.5 border border-stone-100 dark:border-slate-600/50">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-stone-700 dark:text-slate-200 leading-tight line-clamp-1">{v.name}</span>
                <span className={`text-[10px] font-bold ${textColor} shrink-0 ms-1`}>{pct}%</span>
              </div>
              <div className="h-1.5 bg-stone-100 dark:bg-slate-600 rounded-full overflow-hidden mb-1">
                <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-stone-400 dark:text-slate-500">{v.amount} {v.unit}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Per-100g comparison panel ────────────────────────────────────────────────
function Per100gPanel({ result, dir }: { result: HealthScoreResult; dir: "rtl" | "ltr" }) {
  const [open, setOpen] = useState(false);
  if (!result.per100g || !result.servingSize) return null;
  const n = result.per100g;
  const rows = [
    { label: "Calories",       key: "energy",       unit: "kcal" },
    { label: "Protein",        key: "protein",       unit: "g" },
    { label: "Carbohydrates",  key: "carbs",         unit: "g" },
    { label: "Total Fat",      key: "fat",           unit: "g" },
    { label: "Saturated Fat",  key: "saturatedFat",  unit: "g" },
    { label: "Sugars",         key: "sugars",        unit: "g" },
    { label: "Dietary Fiber",  key: "fiber",         unit: "g" },
    { label: "Sodium",         key: "sodium",        unit: "mg" },
    { label: "Potassium",      key: "potassium",     unit: "mg" },
    { label: "Cholesterol",    key: "cholesterol",   unit: "mg" },
    { label: "Calcium",        key: "calcium",       unit: "mg" },
    { label: "Iron",           key: "iron",          unit: "mg" },
    { label: "Magnesium",      key: "magnesium",     unit: "mg" },
    { label: "Phosphorus",     key: "phosphorus",    unit: "mg" },
    { label: "Zinc",           key: "zinc",          unit: "mg" },
  ];
  return (
    <div className="border border-stone-100 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-stone-50 dark:bg-slate-700/50 hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors"
        dir={dir}
      >
        <span className="text-xs font-bold text-stone-600 dark:text-slate-300">Per 100g vs Serving ({result.servingSize}g)</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
      </button>
      {open && (
        <div className="divide-y divide-stone-50 dark:divide-slate-700/50" dir={dir}>
          <div className="grid grid-cols-3 gap-0 px-4 py-2 bg-stone-100 dark:bg-slate-700/80">
            <span className="text-[10px] font-bold text-stone-500 dark:text-slate-400 uppercase">Nutrient</span>
            <span className="text-[10px] font-bold text-stone-500 dark:text-slate-400 uppercase text-center">Per 100g</span>
            <span className="text-[10px] font-bold text-blue-500 uppercase text-center">Per Serving</span>
          </div>
          {rows.map(({ label, key, unit }) => {
            const val100 = n[key] ?? 0;
            const scale = (result.servingSize ?? 100) / 100;
            const valServing = Math.round(val100 * scale * 10) / 10;
            return (
              <div key={key} className="grid grid-cols-3 gap-0 px-4 py-1.5">
                <span className="text-xs text-stone-600 dark:text-slate-300">{label}</span>
                <span className="text-xs text-stone-500 dark:text-slate-400 text-center tabular-nums">{val100} {unit}</span>
                <span className="text-xs text-blue-600 dark:text-blue-400 font-bold text-center tabular-nums">{valServing} {unit}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Full result card ─────────────────────────────────────────────────────────
function ResultCard({ result, t, dir, compact = false }: {
  result: HealthScoreResult; t: (k: string) => string; dir: "rtl" | "ltr"; compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(!compact);
  const dv = result.dailyValues ?? {};

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm overflow-hidden" dir={dir}>
      {/* Score header */}
      <div className="flex items-center gap-4 p-5">
        <ScoreGauge score={result.healthy_score} size={compact ? 100 : 130} />
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-stone-800 dark:text-slate-100 text-base leading-snug mb-1 line-clamp-2">{result.food_name}</h3>
          <ScoreLabel score={result.healthy_score} t={t} />
          {!compact && result.analysis && (
            <p className="text-xs text-stone-500 dark:text-slate-400 mt-1.5 leading-relaxed">{result.analysis}</p>
          )}
        </div>
      </div>

      {compact && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full flex items-center justify-center gap-1 py-2 text-xs text-stone-400 dark:text-slate-500 hover:text-stone-600 dark:hover:text-slate-300 border-t border-stone-100 dark:border-slate-700 transition-colors"
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "إخفاء التفاصيل" : "عرض التفاصيل"}
        </button>
      )}

      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-stone-50 dark:border-slate-700/50">

          {/* Data source badge */}
          <div className="pt-4">
            <DataSourceBadge result={result} dir={dir} />
          </div>

          {/* Serving info */}
          {result.servingDescription && (
            <p className="text-xs text-stone-400 dark:text-slate-500">
              Serving: {result.servingDescription} ({result.servingSize}g)
            </p>
          )}

          {/* Advanced scores row */}
          {(result.nutritionDensity !== undefined || result.proteinQuality) && (
            <div className="grid grid-cols-2 gap-2">
              {result.nutritionDensity !== undefined && (
                <ScoreBadge
                  label="Nutrition Density"
                  value={result.nutritionDensity}
                  icon={<Zap className="w-4 h-4 text-amber-500" />}
                  color="border-amber-100 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5"
                />
              )}
              {result.proteinQuality && (
                <ScoreBadge
                  label="Protein Quality"
                  value={result.proteinQuality}
                  icon={<Star className="w-4 h-4 text-blue-500" />}
                  color="border-blue-100 dark:border-blue-500/20 bg-blue-50/50 dark:bg-blue-500/5"
                />
              )}
            </div>
          )}

          {/* Macros grid */}
          <div>
            <p className="text-[10px] font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide mb-2">Macronutrients</p>
            <div className="grid grid-cols-4 gap-2">
              <NutritionPill label={t("calories")}  value={result.calories}              unit="kcal" color="border-orange-100 dark:border-orange-500/20" dvPct={dv.energy} />
              <NutritionPill label={t("protein")}   value={result.protein}               unit="g"    color="border-blue-100 dark:border-blue-500/20"   dvPct={dv.protein} />
              <NutritionPill label={t("carbs")}     value={result.carbs}                 unit="g"    color="border-amber-100 dark:border-amber-500/20" dvPct={dv.carbs} />
              <NutritionPill label={t("fat")}       value={result.fat}                   unit="g"    color="border-red-100 dark:border-red-500/20"     dvPct={dv.fat} />
              <NutritionPill label={t("sugar")}     value={result.sugar}                 unit="g"    color="border-pink-100 dark:border-pink-500/20"   dvPct={dv.sugars} />
              <NutritionPill label={t("fiber")}     value={result.fiber}                 unit="g"    color="border-green-100 dark:border-green-500/20" dvPct={dv.fiber} />
              {result.saturatedFat !== undefined && (
                <NutritionPill label="Sat. Fat"    value={result.saturatedFat}           unit="g"    color="border-rose-100 dark:border-rose-500/20"   dvPct={dv.saturatedFat} />
              )}
              {result.cholesterol !== undefined && (
                <NutritionPill label="Cholest."    value={result.cholesterol}            unit="mg"   color="border-purple-100 dark:border-purple-500/20" dvPct={dv.cholesterol} />
              )}
            </div>
          </div>

          {/* Electrolytes & minerals grid */}
          {(result.sodium !== undefined || result.potassium !== undefined || result.calcium !== undefined) && (
            <div>
              <p className="text-[10px] font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide mb-2">Minerals & Electrolytes</p>
              <div className="grid grid-cols-4 gap-2">
                <NutritionPill label={t("sodium")}    value={result.sodium}                           unit="mg" color="border-yellow-100 dark:border-yellow-500/20"  dvPct={dv.sodium} />
                {result.potassium  !== undefined && <NutritionPill label="Potassium"  value={result.potassium}  unit="mg" color="border-teal-100 dark:border-teal-500/20"    dvPct={dv.potassium} />}
                {result.calcium    !== undefined && <NutritionPill label="Calcium"    value={result.calcium}    unit="mg" color="border-sky-100 dark:border-sky-500/20"      dvPct={dv.calcium} />}
                {result.iron       !== undefined && <NutritionPill label="Iron"       value={result.iron}       unit="mg" color="border-red-100 dark:border-red-500/20"      dvPct={dv.iron} />}
                {result.magnesium  !== undefined && <NutritionPill label="Magnesium"  value={result.magnesium}  unit="mg" color="border-indigo-100 dark:border-indigo-500/20" dvPct={dv.magnesium} />}
                {result.phosphorus !== undefined && <NutritionPill label="Phosphorus" value={result.phosphorus} unit="mg" color="border-violet-100 dark:border-violet-500/20" dvPct={dv.phosphorus} />}
                {result.zinc       !== undefined && <NutritionPill label="Zinc"       value={result.zinc}       unit="mg" color="border-cyan-100 dark:border-cyan-500/20"     dvPct={dv.zinc} />}
              </div>
            </div>
          )}

          {/* Analysis (compact mode) */}
          {compact && result.analysis && (
            <p className="text-xs text-stone-500 dark:text-slate-400 leading-relaxed">{result.analysis}</p>
          )}

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />{t("scoreWarnings")}
              </p>
              {result.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2">
                  <span className="mt-0.5 shrink-0">⚠️</span>{w}
                </div>
              ))}
            </div>
          )}

          {/* Suggestions */}
          {result.suggestions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-500 dark:text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5 text-emerald-500" />{t("scoreSuggestions")}
              </p>
              {result.suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg px-3 py-2">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />{s}
                </div>
              ))}
            </div>
          )}

          {/* Vitamins grid */}
          {result.vitamins && result.vitamins.length > 0 && (
            <VitaminsGrid vitamins={result.vitamins} dir={dir} label={t("vitaminsSectionLabel")} />
          )}

          {/* Per-100g comparison */}
          {result.per100g && <Per100gPanel result={result} dir={dir} />}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function HealthScoreTab({ lang, t, dir, userGoal = "healthy" }: Props) {
  const [mode, setMode] = useState<"single" | "compare">("single");
  const [foodText, setFoodText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<HealthScoreResult | null>(null);
  const [error, setError] = useState("");
  const [meals, setMeals] = useState<string[]>(["", ""]);
  const [compareResults, setCompareResults] = useState<(HealthScoreResult | null)[]>([null, null]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    const { data } = await supabase
      .from("health_score_analyses")
      .select("id, food_text, result, created_at")
      .eq("device_id", getDeviceId())
      .order("created_at", { ascending: false })
      .limit(10);
    if (data) setHistory(data as HistoryItem[]);
  };

  const saveToHistory = async (foodInput: string, res: HealthScoreResult) => {
    await supabase.from("health_score_analyses").insert({
      device_id: getDeviceId(),
      food_text: foodInput,
      result: res,
    });
    loadHistory();
  };

  const callHealthScore = async (text: string): Promise<HealthScoreResult> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("SIGN_IN_REQUIRED");
    const creditCheck = await checkCreditsForAction(getCreditsForEndpoint("health-score"));
    if (!creditCheck.allowed) throw new Error("LIMIT");
    const res = await fetch(`${supabaseUrl}/functions/v1/health-score`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: authBearerHeader(session), apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
      body: JSON.stringify({ foodText: text, lang, userGoal }),
    });
    if (res.status === 402) throw new Error("LIMIT");
    if (!res.ok) throw new Error("Request failed");
    return res.json();
  };

  const analyze = async () => {
    if (!foodText.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const data = await callHealthScore(foodText.trim());
      setResult(data);
      await saveToHistory(foodText.trim(), data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(msg === "SIGN_IN_REQUIRED"
        ? (lang === "ar" ? "سجّل الدخول لاستخدام ميزات الذكاء الاصطناعي" : "Sign in to use AI features")
        : msg === "LIMIT"
        ? (lang === "ar" ? "لقد وصلت إلى الحد اليومي. اشترك للحصول على استخدام غير محدود." : "You've reached the daily limit. Subscribe for unlimited usage.")
        : (isNetworkError(err) ? t("networkError") : t("scanError")));
    } finally {
      setLoading(false);
    }
  };

  const analyzeCompare = async () => {
    if (meals.filter(m => m.trim()).length < 2) return;
    setCompareLoading(true); setCompareResults(meals.map(() => null));
    try {
      const results = await Promise.all(
        meals.map(m => m.trim() ? callHealthScore(m.trim()).catch(() => null) : Promise.resolve(null))
      );
      setCompareResults(results);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(msg === "SIGN_IN_REQUIRED"
        ? (lang === "ar" ? "سجّل الدخول لاستخدام ميزات الذكاء الاصطناعي" : "Sign in to use AI features")
        : msg === "LIMIT"
        ? (lang === "ar" ? "لقد وصلت إلى الحد اليومي. اشترك للحصول على استخدام غير محدود." : "You've reached the daily limit. Subscribe for unlimited usage.")
        : (isNetworkError(err) ? t("networkError") : t("scanError")));
    } finally {
      setCompareLoading(false);
    }
  };

  const winnerIndex = compareResults.reduce<number>((best, r, i) => {
    if (!r) return best;
    if (best === -1) return i;
    const br = compareResults[best];
    return !br || r.healthy_score > br.healthy_score ? i : best;
  }, -1);

  return (
    <div className="space-y-5 pb-10" dir={dir}>

      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-700 via-teal-700 to-cyan-800 rounded-2xl p-5 text-white shadow-lg">
        <div className="flex items-start gap-3">
          <div className="bg-white/20 rounded-xl p-2.5 shrink-0">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold mb-0.5">{t("scoreTitle")}</h2>
            <p className="text-sm text-white/80">{t("scoreSubtitle")}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 bg-white/15 rounded-lg px-3 py-1.5 text-xs text-white/90">
          <Database className="w-3.5 h-3.5 shrink-0" />
          USDA FoodData Central · Official nutrition data · 26 nutrients tracked
        </div>
        {userGoal && userGoal !== "healthy" && (
          <div className="mt-2 bg-white/15 rounded-lg px-3 py-1.5 text-xs text-white/90 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />{t("scoreGoalNote")}
          </div>
        )}
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 bg-stone-100 dark:bg-slate-800 p-1 rounded-xl">
        {(["single", "compare"] as const).map(m => (
          <button key={m} onClick={() => setMode(m)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${mode === m ? "bg-white dark:bg-slate-700 text-stone-800 dark:text-slate-100 shadow-sm" : "text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-300"}`}>
            {m === "single" ? t("scoreAnalyze") : t("scoreCompare")}
          </button>
        ))}
      </div>

      {/* Single mode */}
      {mode === "single" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm p-4 space-y-3">
            <textarea
              ref={inputRef}
              value={foodText}
              onChange={e => setFoodText(e.target.value)}
              placeholder={t("scoreFoodInput")}
              rows={3}
              className="w-full bg-stone-50 dark:bg-slate-700/50 border border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3 text-sm text-stone-800 dark:text-slate-100 placeholder-stone-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-300 dark:focus:ring-emerald-500 resize-none"
              dir={dir}
              onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) analyze(); }}
            />
            <button onClick={analyze} disabled={loading || !foodText.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm shadow-md hover:shadow-emerald-600/25 transition-all">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? t("scoreAnalyzing") : t("scoreAnalyze")}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-600 dark:text-red-400 flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                {!navigator.onLine ? <WifiOff className="w-4 h-4 shrink-0" /> : <X className="w-4 h-4 shrink-0" />}
                {error}
              </span>
              {!navigator.onLine && (
                <button onClick={() => { setError(""); analyze(); }} className="shrink-0 text-xs font-bold border border-red-300 dark:border-red-500/40 rounded-lg px-2 py-1 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors">{t("retryBtn")}</button>
              )}
            </div>
          )}

          {result && <ResultCard result={result} t={t} dir={dir} />}
        </div>
      )}

      {/* Compare mode */}
      {mode === "compare" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm p-4 space-y-3">
            {meals.map((meal, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-stone-500 dark:text-slate-400">{t("scoreMeal")} {i + 1}</span>
                    {winnerIndex === i && compareResults[i] && (
                      <span className="flex items-center gap-1 text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/15 px-2 py-0.5 rounded-full">
                        <Trophy className="w-3 h-3" />{t("scoreWinner")}
                      </span>
                    )}
                  </div>
                  <input type="text" value={meal} onChange={e => setMeals(m => m.map((v, idx) => idx === i ? e.target.value : v))}
                    placeholder={t("scoreFoodInput")}
                    className="w-full bg-stone-50 dark:bg-slate-700/50 border border-stone-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm text-stone-800 dark:text-slate-100 placeholder-stone-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-300 dark:focus:ring-emerald-500"
                    dir={dir} />
                </div>
                {meals.length > 2 && (
                  <button onClick={() => { setMeals(m => m.filter((_, idx) => idx !== i)); setCompareResults(r => r.filter((_, idx) => idx !== i)); }}
                    className="mt-6 p-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-500 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              {meals.length < 5 && (
                <button onClick={() => { setMeals(m => [...m, ""]); setCompareResults(r => [...r, null]); }}
                  className="flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400 font-medium px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 hover:bg-emerald-100 transition-colors">
                  <Plus className="w-4 h-4" />{t("scoreAddMeal")}
                </button>
              )}
              <button onClick={analyzeCompare} disabled={compareLoading || meals.filter(m => m.trim()).length < 2}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm shadow-md transition-all">
                {compareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
                {compareLoading ? t("scoreAnalyzing") : t("scoreCompareMeals")}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
              {!navigator.onLine ? <WifiOff className="w-4 h-4 shrink-0" /> : <X className="w-4 h-4 shrink-0" />}
              {error}
            </div>
          )}

          {compareResults.some(r => r !== null) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {compareResults.map((r, i) => r ? (
                <div key={i} className={`relative ${winnerIndex === i ? "ring-2 ring-amber-400 dark:ring-amber-500 rounded-2xl" : ""}`}>
                  {winnerIndex === i && (
                    <div className="absolute -top-3 inset-x-0 flex justify-center z-10">
                      <span className="flex items-center gap-1 text-xs font-bold text-white bg-amber-500 px-3 py-0.5 rounded-full shadow">
                        <Trophy className="w-3 h-3" />{t("scoreWinner")}
                      </span>
                    </div>
                  )}
                  <ResultCard result={r} t={t} dir={dir} compact />
                </div>
              ) : null)}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <button onClick={() => setShowHistory(h => !h)}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50 dark:hover:bg-slate-700/50 transition-colors">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-stone-400 dark:text-slate-500" />
              <span className="text-sm font-semibold text-stone-700 dark:text-slate-200">{t("scoreHistory")}</span>
              <span className="text-xs bg-stone-100 dark:bg-slate-700 text-stone-500 dark:text-slate-400 px-2 py-0.5 rounded-full">{history.length}</span>
            </div>
            {showHistory ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          </button>
          {showHistory && (
            <div className="border-t border-stone-100 dark:border-slate-700 divide-y divide-stone-50 dark:divide-slate-700/50">
              {history.map(item => {
                const r = item.result;
                const scoreColor = r.healthy_score >= 75 ? "text-emerald-600 dark:text-emerald-400" : r.healthy_score >= 55 ? "text-lime-600 dark:text-lime-400" : r.healthy_score >= 35 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
                return (
                  <button key={item.id}
                    onClick={() => { setFoodText(item.food_text); setResult(item.result); setMode("single"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-stone-50 dark:hover:bg-slate-700/50 transition-colors text-start" dir={dir}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-stone-700 dark:text-slate-200 truncate">{r.food_name}</p>
                        {r.dataSource === "USDA FoodData Central" && (
                          <span className="text-[9px] font-bold text-blue-500 bg-blue-50 dark:bg-blue-500/10 px-1.5 py-0.5 rounded shrink-0">USDA</span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 dark:text-slate-500 truncate">{item.food_text}</p>
                    </div>
                    <span className={`text-lg font-black ${scoreColor} ms-3 shrink-0`}>{r.healthy_score}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {history.length === 0 && !result && !loading && (
        <div className="text-center py-12 text-stone-400 dark:text-slate-500">
          <Heart className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">{t("scoreNoHistory")}</p>
        </div>
      )}
    </div>
  );
}

// ─── Inline card for ScanTab ──────────────────────────────────────────────────
export function HealthScoreInlineCard({ result, t, dir, loading }: {
  result: HealthScoreResult | null; loading: boolean; t: (k: string) => string; dir: "rtl" | "ltr";
}) {
  if (loading) return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-stone-100 dark:border-slate-700 shadow-sm p-4 flex items-center gap-3" dir={dir}>
      <Loader2 className="w-5 h-5 animate-spin text-emerald-500 shrink-0" />
      <span className="text-sm text-stone-500 dark:text-slate-400">{t("scoreAnalyzing")}</span>
    </div>
  );
  if (!result) return null;

  const color = result.healthy_score >= 75 ? "#16a34a" : result.healthy_score >= 55 ? "#65a30d" : result.healthy_score >= 35 ? "#d97706" : "#dc2626";
  const bgGrad = result.healthy_score >= 75 ? "from-emerald-50 to-green-50 dark:from-emerald-500/10 dark:to-green-500/10 border-emerald-100 dark:border-emerald-500/20" : result.healthy_score >= 55 ? "from-lime-50 to-green-50 dark:from-lime-500/10 dark:to-green-500/10 border-lime-100 dark:border-lime-500/20" : result.healthy_score >= 35 ? "from-amber-50 to-yellow-50 dark:from-amber-500/10 dark:to-yellow-500/10 border-amber-100 dark:border-amber-500/20" : "from-red-50 to-rose-50 dark:from-red-500/10 dark:to-rose-500/10 border-red-100 dark:border-red-500/20";

  return (
    <div className={`bg-gradient-to-br ${bgGrad} rounded-2xl border shadow-sm overflow-hidden`} dir={dir}>
      <div className="flex items-center gap-4 px-4 py-3.5">
        <ScoreGauge score={result.healthy_score} size={88} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Heart className="w-3.5 h-3.5 shrink-0" style={{ color }} />
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color }}>{t("healthScore")}</span>
            {result.dataSource === "USDA FoodData Central" && (
              <span className="text-[9px] font-bold text-blue-500 bg-blue-100 dark:bg-blue-500/20 px-1.5 py-0.5 rounded ms-auto">USDA</span>
            )}
          </div>
          <ScoreLabel score={result.healthy_score} t={t} />
          {result.analysis && (
            <p className="text-xs text-stone-500 dark:text-slate-400 mt-1 leading-relaxed line-clamp-2">{result.analysis}</p>
          )}
        </div>
      </div>

      <div className="px-4 pt-1 pb-1">
        <span className="text-[9px] font-semibold text-stone-400 dark:text-slate-500 uppercase tracking-wide">{dir === "rtl" ? "القيم لكل حصة" : "Values per serving"}</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5 px-4 pb-3">
        {[
          { label: t("calories"), val: result.calories,         unit: "kcal" },
          { label: t("protein"),  val: result.protein,          unit: "g" },
          { label: t("sugar"),    val: result.sugar,            unit: "g" },
          { label: t("sodium"),   val: result.sodium,           unit: "mg" },
        ].map(({ label, val, unit }) => (
          <div key={label} className="bg-white/60 dark:bg-slate-800/60 rounded-xl px-2 py-2 text-center">
            <p className="text-sm font-black text-stone-800 dark:text-slate-100 leading-none tabular-nums">{val ?? "—"}</p>
            <p className="text-[9px] text-stone-400 dark:text-slate-500 mt-0.5">{unit}</p>
            <p className="text-[9px] text-stone-500 dark:text-slate-400 leading-snug">{label}</p>
          </div>
        ))}
      </div>

      {result.warnings.length > 0 && (
        <div className="px-4 pb-3 space-y-1">
          {result.warnings.slice(0, 2).map((w, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{w}
            </div>
          ))}
        </div>
      )}

      {result.vitamins && result.vitamins.length > 0 && (
        <div className="px-4 pb-4">
          <VitaminsGrid vitamins={result.vitamins} dir={dir} label={t("vitaminsSectionLabel")} />
        </div>
      )}
    </div>
  );
}
